import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { REQUEST } from '@angular/core';

const COOKIE_KEY = 'demo_unlocked';
const MAIN_HOST = 'truckhandel.ch';
const EXCLUDED_SUBDOMAINS = new Set(['cdn', 'www']);

@Injectable({ providedIn: 'root' })
export class DemoAccessService {
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly request = inject(REQUEST, { optional: true });

  /** The subdomain for this demo page (e.g. "grebo"), or null on the main domain. */
  readonly subdomain: string | null = this.detectSubdomain();

  private readonly unlockedSig = signal(this.readUnlockCookie());
  readonly isUnlocked = this.unlockedSig.asReadonly();

  private detectSubdomain(): string | null {
    const hostname = this.doc.location?.hostname ?? '';
    if (!hostname.endsWith(`.${MAIN_HOST}`)) return null;
    const sub = hostname.slice(0, hostname.length - MAIN_HOST.length - 1);
    return sub && !EXCLUDED_SUBDOMAINS.has(sub) ? sub : null;
  }

  private readUnlockCookie(): boolean {
    const cookies = isPlatformBrowser(this.platformId)
      ? this.doc.cookie
      : (this.request?.headers.get('cookie') ?? '');
    return cookies.split(';').some(c => c.trim() === `${COOKIE_KEY}=1`);
  }

  /**
   * Tries to unlock access using the given password.
   * The password for a demo subdomain is the subdomain name itself (e.g. "grebo").
   * Returns true if the password was correct.
   */
  tryUnlock(password: string): boolean {
    if (!this.subdomain) return false;
    if (password.trim().toLowerCase() !== this.subdomain.toLowerCase()) return false;

    this.doc.cookie = `${COOKIE_KEY}=1; path=/; SameSite=Strict`;
    this.unlockedSig.set(true);
    return true;
  }
}
